"use client";
import { useTranslations } from "next-intl";
import { Logger } from "@shared/utils/logger";
import { JSX, useEffect, useMemo, useState } from "react";
import { BackgroundProcessor } from "@livekit/track-processors";
import { Ban, Sparkles, ChevronDown, Check, VideoOff } from "lucide-react";
import { isLocalTrack, LocalTrackPublication, Track, LocalVideoTrack } from "livekit-client";
import Nature from "../../../../../public/background-images/ali-kazal-tbw_KQE3Cbg-unsplash.jpg";
import Desk from "../../../../../public/background-images/samantha-gades-BlIhVfXbi9s-unsplash.jpg";
import { VideoTrack, TrackReference, useLocalParticipant, useMediaDeviceSelect } from "@livekit/components-react";

/** Predefined background images for virtual background */
const BACKGROUND_IMAGES = [
  { name: "Desk", path: Desk },
  { name: "Nature", path: Nature }
];

/** CameraSettings - Component for camera settings and background effects */
type BackgroundType = "none" | "blur" | "image";

export const CameraSettings = (): JSX.Element => {
  const t = useTranslations();
  const { cameraTrack, localParticipant } = useLocalParticipant();

  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind: "videoinput" });
  const [isDeviceMenuOpen, setIsDeviceMenuOpen] = useState(false);

  // Helper to determine initial background type safely
  const getInitialBackgroundType = (): BackgroundType => {
    const processor = (cameraTrack as LocalTrackPublication)?.track?.getProcessor();
    // Check processor name or type if available, otherwise default to "none"
    if (processor?.name === "background-blur") return "blur";
    if (processor?.name === "virtual-background") return "image";
    return "none";
  };

  const [backgroundType, setBackgroundType] = useState<BackgroundType>(getInitialBackgroundType());

  const [virtualBackgroundImagePath, setVirtualBackgroundImagePath] = useState<string | null>(null);

  const activeDeviceLabel = useMemo(() => {
    const label = devices.find((d) => d.deviceId === activeDeviceId)?.label;
    return label === "Default" || label === "System Default" ? t("common.default") : label || t("settings.cameraLabel");
  }, [devices, activeDeviceId, t]);

  const camTrackRef: TrackReference | undefined = useMemo(() => {
    return cameraTrack
      ? {
          participant: localParticipant,
          publication: cameraTrack,
          source: Track.Source.Camera
        }
      : undefined;
  }, [localParticipant, cameraTrack]);

  const selectBackground = (type: BackgroundType, imagePath?: string) => {
    setBackgroundType(type);
    if (type === "image" && imagePath) {
      setVirtualBackgroundImagePath(imagePath);
    } else if (type !== "image") {
      setVirtualBackgroundImagePath(null);
    }
  };

  /**
   * Apply background processor.
   * Handles switching between None, Blur, and Image backgrounds cleanly.
   */
  useEffect(() => {
    const track = cameraTrack?.track;
    if (!isLocalTrack(track)) return;

    const applyProcessor = async () => {
      try {
        // Stop any existing processor first
        await (track as LocalVideoTrack).stopProcessor();

        if (backgroundType === "blur") {
          await (track as LocalVideoTrack).setProcessor(
            BackgroundProcessor({ mode: "background-blur", blurRadius: 10 })
          );
        } else if (backgroundType === "image" && virtualBackgroundImagePath) {
          await (track as LocalVideoTrack).setProcessor(
            BackgroundProcessor({ mode: "virtual-background", imagePath: virtualBackgroundImagePath })
          );
        }
      } catch (error) {
        Logger.error("CameraSettings", "Failed to apply background processor:", error);
        // Reset to none on error
        setBackgroundType("none");
      }
    };

    applyProcessor();
  }, [cameraTrack, backgroundType, virtualBackgroundImagePath]);

  return (
    <div className="flex flex-col gap-5">
      {/* Video Preview */}
      <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-black/5 ring-1 ring-black/10">
        {camTrackRef && !cameraTrack?.isMuted ? (
          <VideoTrack className="w-full h-full object-cover scale-x-[-1]" trackRef={camTrackRef} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-100">
            <VideoOff className="w-8 h-8 mb-2 opacity-50" />
            <span className="text-sm font-medium">{t("settings.cameraOffLabel")}</span>
          </div>
        )}
      </div>

      {/* Input Selection */}
      <section className="relative w-full">
        <button
          onClick={() => setIsDeviceMenuOpen(!isDeviceMenuOpen)}
          className={`w-full flex items-center justify-between p-3 rounded-xl bg-white border border-gray-200 text-gray-900 shadow-sm hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group ${
            isDeviceMenuOpen ? "ring-2 ring-primary/10 border-primary" : ""
          }`}
        >
          <span className="font-medium truncate max-w-[200px]" title={activeDeviceLabel}>
            {activeDeviceLabel}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${isDeviceMenuOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isDeviceMenuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsDeviceMenuOpen(false)} aria-hidden="true" />
            <div className="absolute top-full left-0 w-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
              <ul className="py-1 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                {devices.map((device) => (
                  <li key={device.deviceId}>
                    <button
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                        device.deviceId === activeDeviceId
                          ? "bg-primary/5 text-primary font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setActiveMediaDevice(device.deviceId);
                        setIsDeviceMenuOpen(false);
                      }}
                    >
                      <span className="truncate pr-4">
                        {device.label === "Default" || device.label === "System Default"
                          ? t("common.default")
                          : device.label}
                      </span>
                      {device.deviceId === activeDeviceId && <Check className="w-4 h-4 shrink-0" />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </section>

      <div className="space-y-3">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest pl-1">
          {t("settings.backgroundEffects")}
        </label>

        <div className="grid grid-cols-2 gap-3">
          {/* None Option */}
          <button
            onClick={() => selectBackground("none")}
            className={`relative group aspect-video rounded-xl border-2 transition-all duration-200 overflow-hidden ${
              backgroundType === "none"
                ? "border-primary ring-2 ring-primary/20"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 group-hover:bg-gray-100 transition-colors">
              <Ban className={`w-6 h-6 mb-2 ${backgroundType === "none" ? "text-primary" : "text-gray-400"}`} />
              <span className={`text-xs font-medium ${backgroundType === "none" ? "text-primary" : "text-gray-500"}`}>
                {t("settings.none")}
              </span>
            </div>
          </button>

          {/* Blur Option */}
          <button
            onClick={() => selectBackground("blur")}
            className={`relative group aspect-video rounded-xl border-2 transition-all duration-200 overflow-hidden ${
              backgroundType === "blur"
                ? "border-primary ring-2 ring-primary/20"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className="absolute inset-0 bg-linear-to-br from-gray-200 to-gray-300">
              <div className="absolute inset-0 backdrop-blur-md" />
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
              <Sparkles className={`w-6 h-6 mb-2 ${backgroundType === "blur" ? "text-primary" : "text-gray-600"}`} />
              <span
                className={`text-xs font-medium bg-white/90 px-2 py-0.5 rounded-md ${
                  backgroundType === "blur" ? "text-primary" : "text-gray-700"
                }`}
              >
                {t("settings.blur")}
              </span>
            </div>
          </button>

          {/* Image Options */}
          {BACKGROUND_IMAGES.map((image) => (
            <button
              key={image.path.src}
              onClick={() => selectBackground("image", image.path.src)}
              className={`relative group aspect-video rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                backgroundType === "image" && virtualBackgroundImagePath === image.path.src
                  ? "border-primary ring-2 ring-primary/20"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: `url(${image.path.src})` }}
              />
              <div
                className={`absolute inset-0 transition-opacity duration-200 ${
                  backgroundType === "image" && virtualBackgroundImagePath === image.path.src
                    ? "bg-primary/10"
                    : "bg-black/0 group-hover:bg-black/10"
                }`}
              />

              <div className="absolute bottom-2 left-2 right-2 flex justify-center">
                <span className="text-xs font-medium bg-black/60 text-white px-2 py-0.5 rounded-md backdrop-blur-sm truncate max-w-full">
                  {image.name === "Desk" ? t("settings.desk") : t("settings.nature")}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
